import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AcknowledgementSection } from '../Summary/AcknowledgementSection';
import { Text } from '../../pulsechain_components/Text';
import { Button } from '../../pulsechain_components/Button';
import { WorkflowPageTemplate } from '../../pulsechain_components/WorkflowPage/WorkflowPageTemplate';
import useIntlNetworkName from '../../hooks/useIntlNetworkName';
import {
  RPC_URL,
  RPC_CHAIN_ID,
  TICKER_NAME,
  ETHERSCAN_URL,
  NETWORK_NAME,
} from '../../utils/envVars';

export const WrongNetwork = () => {
  const { formatMessage } = useIntl();
  const { executionLayerName } = useIntlNetworkName();
  const [isAdding, setIsAdding] = useState(false);
  const [addSuccess, setAddSuccess] = useState(false);

  const acknowledgementTitle = formatMessage({
    defaultMessage: 'Your network has changed',
  });

  const pageTitle = formatMessage({
    defaultMessage: 'Deposit summary',
  });

  const addPulseChainNetwork = async () => {
    if (!window.ethereum) {
      alert('MetaMask is not installed!');
      return;
    }

    setIsAdding(true);

    try {
      // First try to switch to the network
      await (window.ethereum as any).request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${parseInt(RPC_CHAIN_ID, 10).toString(16)}` }],
      });
      setAddSuccess(true);
    } catch (switchError) {
      // Network doesn't exist, so add it
      const switchErr = switchError as any;
      if (switchErr.code === 4902) {
        try {
          await (window.ethereum as any).request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `0x${parseInt(RPC_CHAIN_ID, 10).toString(16)}`,
                chainName: NETWORK_NAME,
                nativeCurrency: {
                  name: TICKER_NAME,
                  symbol: TICKER_NAME,
                  decimals: 18,
                },
                rpcUrls: [RPC_URL],
                blockExplorerUrls: [ETHERSCAN_URL.replace('/tx', '')],
              },
            ],
          });
          setAddSuccess(true);
        } catch (addError) {
          console.error('Failed to add network:', addError);
          alert('Failed to add network. Please add it manually.');
        }
      } else {
        console.error('Failed to switch network:', switchError);
        alert('Failed to switch network. Please try again.');
      }
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <WorkflowPageTemplate title={pageTitle}>
      <AcknowledgementSection title={acknowledgementTitle}>
        <Text className="mb20">
          <FormattedMessage
            defaultMessage="Your wallet is on the wrong PulseChain network. To continue, connect to {executionLayerName}."
            values={{ executionLayerName }}
          />
        </Text>

        {!addSuccess && (
          <div className="flex center mt20">
            <Button
              rainbow
              label={
                isAdding
                  ? formatMessage({ defaultMessage: 'Adding Network...' })
                  : formatMessage(
                      {
                        defaultMessage: 'Add {networkName} to MetaMask',
                      },
                      { networkName: NETWORK_NAME }
                    )
              }
              onClick={addPulseChainNetwork}
              disabled={isAdding}
              width={300}
            />
          </div>
        )}

        {addSuccess && (
          <div className="flex center mt20">
            <Text color="greenLight" size="large" className="mb10">
              <FormattedMessage
                defaultMessage="✅ {networkName} added successfully! Please refresh the page."
                values={{ networkName: NETWORK_NAME }}
              />
            </Text>
          </div>
        )}

        <Text className="mt20" size="small" color="blueLight">
          <FormattedMessage
            defaultMessage="Network Details: Chain ID {chainId} • RPC: {rpcUrl}"
            values={{
              chainId: RPC_CHAIN_ID,
              rpcUrl: RPC_URL,
            }}
          />
        </Text>
      </AcknowledgementSection>
    </WorkflowPageTemplate>
  );
};
